import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { OpenLikeStatuses } from "@redotech/redo-fulfillment-group-common/fulfillment-group-status";
import { OMSUser } from "@redotech/redo-fulfillment-group-common/oms-user";
import { PickSessionStrategy } from "@redotech/redo-fulfillment-wms-common/pick-session-enums";
import { resolveForcedPickSessionStrategy } from "@redotech/redo-fulfillment-wms-common/wms-constants";
import type { LocationId } from "@redotech/redo-inventory-id";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { ArrayStringFilterOperator } from "@redotech/redo-views-common/advanced-filters/array-string-filter";
import {
  AdvancedFilterType,
  FilterOperator,
} from "@redotech/redo-views-common/advanced-filters/generic-advanced-filter-data";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import { useDeviceLocation } from "../hooks/use-device-location";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";
import { usePickerZone } from "../wms/pick-sessions/use-picker-zone";
import { RejectedShipmentRow } from "./rejected-shipments-modal";

const PICK_SESSION_MAX_GROUPS = 100;

export interface CreatePickSessionResult {
  pickSessionId: string | null;
  rejectedRows: RejectedShipmentRow[];
}

interface CreatePickSessionFromOrdersModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Fired after a successful RPC roundtrip with either a created pick
   * session, a set of rejected shipments, or both. The parent is responsible
   * for closing the modal (we no longer auto-close) and surfacing toasts. */
  onResult: (result: CreatePickSessionResult) => void;
  fulfillmentGroups: FulfillmentGroupData[];
  users: OMSUser[];
}

interface EligibilityBreakdown {
  eligible: number;
  alreadyPicked: number;
  notOpen: number;
  wrongLocation: number;
}

function computeEligibility(
  groups: FulfillmentGroupData[],
  locationId: string | undefined,
): EligibilityBreakdown {
  let eligible = 0;
  let alreadyPicked = 0;
  let notOpen = 0;
  let wrongLocation = 0;
  for (const group of groups) {
    if (group.pickSessionId) {
      alreadyPicked++;
      continue;
    }
    if (!OpenLikeStatuses.includes(group.status)) {
      notOpen++;
      continue;
    }
    if (
      locationId &&
      group.originAddress?.locationId &&
      group.originAddress.locationId !== locationId
    ) {
      wrongLocation++;
      continue;
    }
    eligible++;
  }
  return { eligible, alreadyPicked, notOpen, wrongLocation };
}

export const CreatePickSessionFromOrdersModal = memo(
  function CreatePickSessionFromOrdersModal({
    isOpen,
    onClose,
    onResult,
    fulfillmentGroups,
    users,
  }: CreatePickSessionFromOrdersModalProps) {
    const outboundLabelsRpcClient = useRequiredContext(
      RedoOutboundLabelsRpcClientContext,
    );
    const team = useRequiredContext(TeamContext);
    const currentUser = useRequiredContext(UserContext);

    // Default = device's stored location (or team default external-id fallback).
    // We intentionally discard the setter — changing the location for this one
    // session must not persist back to the device.
    const [deviceLocationId] = useDeviceLocation();
    const [selectedLocation, setSelectedLocation] = useState<
      RedoListItem<string> | undefined
    >(undefined);
    const [selectedUserId, setSelectedUserId] = useState<string | undefined>(
      undefined,
    );
    const [isSubmitting, setIsSubmitting] = useState(false);
    const pickerZone = usePickerZone(
      (selectedLocation?.value ?? null) as LocationId | null,
    );
    const pickerZoneId =
      pickerZone.enabled && pickerZone.zoneId ? pickerZone.zoneId : null;

    const userOptions: RedoListItem<string>[] = useMemo(() => {
      const filtered = users.filter((u) => u._id !== "unassigned");
      // The current user isn't always in team.users (e.g., admins, 3PL ops on
      // a managed merchant's table). Make sure they appear so they can assign
      // a session to themselves.
      const currentUserId = currentUser._id.toString();
      const includesCurrent = filtered.some((u) => u._id === currentUserId);
      const currentUserDisplayName =
        currentUser.name?.trim() ||
        [currentUser.firstName, currentUser.lastName]
          .filter((v) => !!v)
          .join(" ")
          .trim() ||
        currentUser.email ||
        "You";
      const all = includesCurrent
        ? filtered
        : [
            { _id: currentUserId, name: currentUserDisplayName } as OMSUser,
            ...filtered,
          ];
      return all.map((u) => ({
        id: u._id,
        type: "text" as const,
        text: u.name,
        value: u._id,
      }));
    }, [
      users,
      currentUser._id,
      currentUser.name,
      currentUser.firstName,
      currentUser.lastName,
      currentUser.email,
    ]);

    const selectedUser = useMemo(() => {
      const currentUserId = currentUser._id.toString();
      return (
        userOptions.find((o) => o.value === selectedUserId) ??
        userOptions.find((o) => o.value === currentUserId) ??
        userOptions[0]
      );
    }, [userOptions, selectedUserId, currentUser._id]);

    const locationsLoad = useLoad(
      async (signal) => {
        const { locations } = await outboundLabelsRpcClient.getCrdbLocations(
          null,
          { signal },
        );
        return locations;
      },
      [outboundLabelsRpcClient],
    );

    const locationOptions: RedoListItem<string>[] = useMemo(() => {
      const options = (locationsLoad.value ?? []).map((location) => ({
        id: location.id,
        type: "text" as const,
        text: location.name,
        value: location.id,
      }));
      // Initialize selected location from device once locations are loaded
      if (options.length > 0 && !selectedLocation) {
        const defaultOption =
          options.find((o) => o.value === deviceLocationId) ?? options[0];

        setSelectedLocation(defaultOption);
      }
      return options;
    }, [locationsLoad.value, deviceLocationId, selectedLocation]);

    // FulfillmentGroup.summary.originAddress.locationId is the upstream/external
    // location id (e.g. Shopify location id), not the CRDB `loc_...` id. The
    // dropdown sends the CRDB id (required by createPickSession). For the
    // client-side eligibility comparison we map CRDB id → externalId so we're
    // comparing apples to apples.
    const selectedLocationExternalId = useMemo(() => {
      if (!selectedLocation?.value) {
        return undefined;
      }
      return (
        locationsLoad.value?.find((l) => l.id === selectedLocation.value)
          ?.externalId ?? undefined
      );
    }, [locationsLoad.value, selectedLocation?.value]);

    const eligibility = useMemo(
      () => computeEligibility(fulfillmentGroups, selectedLocationExternalId),
      [fulfillmentGroups, selectedLocationExternalId],
    );

    const eligibleGroups = useMemo(
      () =>
        fulfillmentGroups.filter((g) => {
          if (g.pickSessionId) {
            return false;
          }
          if (!OpenLikeStatuses.includes(g.status)) {
            return false;
          }
          if (
            selectedLocationExternalId &&
            g.originAddress?.locationId &&
            g.originAddress.locationId !== selectedLocationExternalId
          ) {
            return false;
          }
          return true;
        }),
      [fulfillmentGroups, selectedLocationExternalId],
    );
    const eligibleIds = useMemo(
      () => eligibleGroups.map((g) => g._id),
      [eligibleGroups],
    );

    // Strategy decision matrix for this hand-picked flow:
    //   all single-item    → SINGLE_ITEM (the server's `expandedItemQuantity
    //                        == 1` filter accepts every selection)
    //   any multi-item     → MULTI_ITEM, regardless of whether the rest are
    //                        single-item or multi-item
    //
    // For the mixed case, we'd lose single-item orders to the server's
    // default `expandedItemQuantity > 1` MULTI filter — but the modal
    // always sends `allowSingleItemInMultiSession: true` (see the RPC call
    // below), which makes the server drop that filter for MULTI sessions.
    // Net result: every eligible selection lands in the session, regardless
    // of mix. The SINGLE branch above is left strict because multi-item
    // orders genuinely cannot be picked under the single-item flow.
    //
    // forceMultiItemPickSessions on the team always produces MULTI; the
    // server bypasses the strategy filter under that flag too, so we don't
    // need to compute "all single-item" in that branch.
    const { forceMultiItem, forceSingleItem } =
      resolveForcedPickSessionStrategy(team?.settings?.outboundLabels?.wms);
    const strategy = useMemo(() => {
      if (forceSingleItem) {
        return PickSessionStrategy.SINGLE_ITEM;
      }
      if (forceMultiItem || eligibleGroups.length === 0) {
        return PickSessionStrategy.MULTI_ITEM;
      }
      const allSingleItem = eligibleGroups.every((g) => {
        const totalQty = (g.items ?? []).reduce(
          (sum, item) => sum + (item.quantity ?? 0),
          0,
        );
        return totalQty === 1;
      });
      return allSingleItem
        ? PickSessionStrategy.SINGLE_ITEM
        : PickSessionStrategy.MULTI_ITEM;
    }, [forceMultiItem, forceSingleItem, eligibleGroups]);

    const handleClose = useHandler(() => {
      setIsSubmitting(false);
      onClose();
    });

    const handleConfirm = useHandler(async () => {
      if (
        !selectedLocation?.value ||
        !selectedUser?.value ||
        eligibleIds.length === 0
      ) {
        return;
      }
      if (pickerZone.pending) {
        toast("Zones are still loading. Try again in a moment.", {
          variant: "error",
        });
        return;
      }
      if (pickerZone.blocked) {
        toast(
          "Zones could not be loaded for this location. A zone is required to start a pick session.",
          { variant: "error" },
        );
        return;
      }
      setIsSubmitting(true);
      try {
        const result = await outboundLabelsRpcClient.createPickSession({
          numberOfGroups: Math.min(eligibleIds.length, PICK_SESSION_MAX_GROUPS),
          searchInput: {
            // The handler clears advancedFiltersData before searching — the
            // FG-id filter has to go through filterGroup. We also pass a
            // non-null filterName below so the server uses this filterGroup
            // instead of falling back to the team's default pick-session view.
            advancedFiltersData: [],
            filterGroup: {
              operator: FilterOperator.AND,
              clauses: [
                {
                  type: AdvancedFilterType.ARRAY_TO_STRING,
                  name: "fulfillmentGroupId",
                  value: eligibleIds,
                  operator: ArrayStringFilterOperator.ANY_OF,
                },
              ],
            },
            merchantTimezone: team.ianaTimezone,
          },
          seedGroupId: null,
          pickerZoneId,
          locationId: selectedLocation.value as LocationId,
          strategy,
          assignedUserId: selectedUser.value,
          // Mark the session as explicitly queued — surfaces it under
          // "New assignments" on the assignee's pick-sessions landing page
          // until they actually scan something.
          assigningUserId: currentUser._id.toString(),
          filterName: "Selected orders",
          useIncremental: true,
          pollForOpenSearchChanges: true,
          // Bypass the `expandedItemQuantity > 1` strategy filter so a
          // single-item order included in this hand-picked selection is
          // folded into the multi session instead of being silently
          // filtered out. SINGLE sessions remain strict.
          allowSingleItemInMultiSession: true,
        });

        const rejectedRows = (result.rejectedGroups ?? []).map((g) => ({
          groupId: g.groupId.toString(),
          orderName: g.orderNames.join(", ") || g.groupId.toString(),
          reason: g.reason,
          productSku: g.productSku,
          productVariantTitle: g.productVariantTitle,
          productImageUrl: g.productImageUrl,
        }));

        if (!result.pickSessionId && rejectedRows.length === 0) {
          // Defensive — RPC returned nothing useful, surface a generic error
          // and stay open so the user can retry.
          toast("Failed to create pick session", { variant: "error" });
          return;
        }

        // Hand the outcome off to the parent — it owns the close, the toasts,
        // and the optional rejection modal.
        onResult({ pickSessionId: result.pickSessionId ?? null, rejectedRows });
      } catch (error) {
        console.error("Failed to create pick session from orders:", error);
        toast("Failed to create pick session", { variant: "error" });
      } finally {
        setIsSubmitting(false);
      }
    });

    const canCreate =
      eligibility.eligible > 0 &&
      !!selectedLocation?.value &&
      !!selectedUser?.value &&
      !pickerZone.pending &&
      !pickerZone.blocked;

    const overCap = eligibility.eligible > PICK_SESSION_MAX_GROUPS;
    const willBePicked = Math.min(
      eligibility.eligible,
      PICK_SESSION_MAX_GROUPS,
    );

    return (
      <RedoModal
        isOpen={isOpen}
        modalSize="sm"
        onModalCloseRequested={handleClose}
        subtitle="Create a new pick session from the selected fulfillments. Fulfillments that are already picked, closed, or at a different location will be excluded."
        title="Create pick session"
      >
        <Flex dir="column" gap="xl" w="full">
          <Flex dir="column" gap="md" w="full">
            <RedoSingleSelectDropdownInput
              label="Location"
              options={locationOptions}
              optionSelected={({ item }) => setSelectedLocation(item)}
              placeholder="Select location..."
              selectedItem={selectedLocation}
              size="xs"
            />
          </Flex>

          <Flex dir="column" gap="md" w="full">
            <RedoSingleSelectDropdownInput
              label="Assignee"
              options={userOptions}
              optionSelected={({ item }) => setSelectedUserId(item.value)}
              placeholder="Select assignee..."
              selectedItem={selectedUser}
              size="xs"
            />
          </Flex>

          <Flex dir="column" gap="sm" w="full">
            <Text fontSize="sm" fontWeight="semibold" textColor="primary">
              {willBePicked === 1
                ? "1 fulfillment will be picked"
                : `${willBePicked} fulfillments will be picked`}
            </Text>
            {overCap && (
              <Text fontSize="sm" textColor="warning">
                {`Only the first ${PICK_SESSION_MAX_GROUPS} of ${eligibility.eligible} eligible fulfillments will be added to this session. Create additional sessions for the rest.`}
              </Text>
            )}
            {eligibility.alreadyPicked > 0 && (
              <Text fontSize="sm" textColor="secondary">
                {`${eligibility.alreadyPicked} already in a pick session`}
              </Text>
            )}
            {eligibility.notOpen > 0 && (
              <Text fontSize="sm" textColor="secondary">
                {`${eligibility.notOpen} not open`}
              </Text>
            )}
            {eligibility.wrongLocation > 0 && (
              <Text fontSize="sm" textColor="secondary">
                {`${eligibility.wrongLocation} at a different location`}
              </Text>
            )}
          </Flex>

          <Flex gap="lg" justify="flex-end" w="full">
            <RedoButton
              hierarchy="secondary"
              onClick={handleClose}
              size="md"
              text="Cancel"
            />
            <RedoButton
              disabled={!canCreate || isSubmitting}
              hierarchy="primary"
              onClick={handleConfirm}
              pending={isSubmitting}
              size="md"
              text="Create pick session"
            />
          </Flex>
        </Flex>
      </RedoModal>
    );
  },
);
